import React from 'react';
import ImportModal from '../common/ImportModal';
import { useTranslation } from 'react-i18next';

/**
 * Opening-stock import.
 *
 * One sheet covers every product in every warehouse, and BOTH ways stock is held —
 * which one applies is a property of the product, not of the file:
 *
 *   ordinary product  ->  one row carrying stock_quantity
 *   coil product      ->  one row PER COIL (batch_number / weight_kg / length_m)
 *
 * So most columns are marked optional: each row fills in only the half that applies to
 * its product. The importer decides which half it needed and says so by row number when
 * the wrong one is filled in, because "required" here depends on a value the template
 * cannot see.
 *
 * Header validation is left ON (the other import modals skip it). It only checks that
 * the two genuinely required columns are present, which is worth catching before an
 * upload rather than after.
 */
const InventoryImportModal = ({
  isOpen,
  onClose,
  onImport,
  importing = false,
  importError = null,
  importSuccess = false,
}) => {
  const { t } = useTranslation();

  const NOTES = t('importTemplate.notes', { returnObjects: true });

  const TEMPLATE_HEADERS = [
    'warehouse_name',
    'alias_name',
    'stock_quantity',
    'batch_number',
    'weight_kg',
    'length_m',
    'colour',
    'supplier_batch_ref',
    'received_date',
    'minimum_stock',
  ];

  const TEMPLATE_COLUMN_SPECS = {
    warehouse_name: { required: true, note: NOTES.matchWarehouse, example: 'Main Warehouse' },
    alias_name: { required: true, note: NOTES.matchProduct, example: 'AMNS 0.50X1220 BLUE' },
    stock_quantity: { required: false, note: NOTES.stockAbsolute, example: '500' },
    batch_number: { required: false, note: NOTES.coilBatch, example: 'B-1024' },
    weight_kg: { required: false, note: NOTES.coilWeight, example: '600' },
    length_m: { required: false, note: NOTES.coilLength, example: '120' },
    colour: { required: false, note: NOTES.coilOptional, example: 'blue' },
    supplier_batch_ref: { required: false, note: NOTES.coilOptional, example: 'M/42' },
    received_date: { required: false, note: NOTES.coilReceivedDate, example: '2026-09-05' },
    minimum_stock: { required: false, note: NOTES.minimumStock, example: '50' },
  };

  const importConfig = t('warehouseInventory.import.config', { returnObjects: true });

  return (
    <ImportModal
      isOpen={isOpen}
      onClose={onClose}
      onImport={onImport}
      importing={importing}
      importError={importError}
      importSuccess={importSuccess}
      templateHeaders={TEMPLATE_HEADERS}
      templateColumnSpecs={TEMPLATE_COLUMN_SPECS}
      templateFilename={importConfig?.templateFilename || 'inventory_import_template'}
      inputId={importConfig?.inputId || 'inventory-import-input'}
    />
  );
};

export default InventoryImportModal;
